/**
 * WonderWorldReport.js
 *
 * All parent-facing report logic extracted from App.js.
 * Previously: getCognitiveType, generateSummary, generateTrendReport,
 * buildCurrentReportText, downloadReportTxt all lived inside the App() function,
 * interleaved with child game state.
 *
 * Now: pure functions that take data as arguments — no direct state access.
 * App.js or useWonderWorld passes the needed data in; nothing is closed over.
 *
 * Usage in App.js:
 *
 *   import { downloadReportTxt, buildCurrentReportText } from "./WonderWorldReport";
 *
 *   // In handleArchiveAndClear:
 *   downloadReportTxt({ history, lang, t, pickInline, getWorldDisplayName });
 */

// ─── Cognitive profile classification ────────────────────────────────────────

/**
 * Returns the cognitive profile type key for a session or trend report.
 * @param {{ exploration, analysis, risk, curiosity, care, logic }} report
 * @returns {"explorer"|"analyst"|"adventurer"|"helper"|"balanced"}
 */
export function getCognitiveType(report) {
  if (report.exploration >= 3 && report.curiosity >= 2) return "explorer";
  if (report.analysis >= 3 || report.logic >= 2) return "analyst";
  if (report.risk >= 3) return "adventurer";
  if (report.care >= 2) return "helper";
  return "balanced";
}

/**
 * Returns the localized display label for a cognitive type key.
 */
export function getCognitiveTypeLabel(typeKey, lang) {
  const labels = {
    en: {
      explorer: "Explorer",
      analyst: "Analyst",
      adventurer: "Adventurer",
      helper: "Helper",
      balanced: "Balanced Thinker"
    },
    zh: {
      explorer: "探索者",
      analyst: "分析者",
      adventurer: "冒险者",
      helper: "关怀者",
      balanced: "平衡思考者"
    },
    fr: {
      explorer: "Explorateur",
      analyst: "Analyste",
      adventurer: "Aventurier",
      helper: "Protecteur",
      balanced: "Penseur équilibré"
    }
  };
  return labels[lang]?.[typeKey] || labels.en[typeKey] || typeKey;
}

// ─── Session narrative ────────────────────────────────────────────────────────

/**
 * Generates the free-text narrative for a single session.
 * @param {import("./wonderWorldProgress").SessionEntry} report
 * @param {{ lang: string, pickInline: Function }} ctx
 */
export function generateSummary(report, { lang, pickInline }) {
  let text = "";
  const reportQuestions = report.questions ?? report.questionCount ?? 0;
  const reportAccuracyRate = report.accuracyRate ?? 0;
  const accuracyBand =
    report.accuracy ??
    (reportAccuracyRate > 80 ? "strong" : reportAccuracyRate > 50 ? "good" : "developing");

  text += pickInline("Session Summary:\n\n", "Résumé de la session :\n\n");

  if (reportQuestions === 0) {
    text += pickInline(
      "No playable data has been recorded yet. Let your child complete at least one question in the story mode, then return to this report page.",
      "Aucune donnée de jeu n'a encore été enregistrée. Laissez votre enfant terminer au moins une question en mode histoire, puis revenez à cette page."
    );
    return text;
  }

  if (accuracyBand === "strong") {
    text += pickInline(
      "Your child demonstrated strong mathematical reasoning during this session. ",
      "Votre enfant a montré un très bon raisonnement mathématique pendant cette session. "
    );
  } else if (accuracyBand === "good") {
    text += pickInline(
      "Your child showed solid problem-solving ability with room for improvement. ",
      "Votre enfant a montré de bonnes capacités de résolution de problèmes, avec encore une marge de progression. "
    );
  } else {
    text += pickInline(
      "Your child is still developing confidence in solving numerical challenges. ",
      "Votre enfant continue de construire sa confiance face aux défis numériques. "
    );
  }

  text += pickInline("\n\nCognitive Tendencies:\n\n", "\n\nTendances cognitives :\n\n");

  if (report.exploration > report.analysis) {
    text += pickInline(
      "Your child shows a strong exploration tendency and appears motivated by discovery and new environments. ",
      "Votre enfant montre une forte tendance à l'exploration et semble motivé par la découverte de nouveaux environnements. "
    );
  }

  if (report.analysis >= report.exploration) {
    text += pickInline(
      "Your child demonstrates analytical thinking and tends to observe situations carefully before acting. ",
      "Votre enfant fait preuve d'une pensée analytique et prend souvent le temps d'observer avant d'agir. "
    );
  }

  if (report.risk > 2) {
    text += pickInline(
      "They appear comfortable making bold decisions in uncertain situations. ",
      "Votre enfant semble à l'aise pour prendre des décisions audacieuses dans des situations incertaines. "
    );
  } else {
    text += pickInline(
      "They tend to make careful and thoughtful decisions. ",
      "Votre enfant a tendance à prendre des décisions prudentes et réfléchies. "
    );
  }

  if (report.curiosity > 2) {
    text += pickInline(
      "Curiosity appears to be a visible strength in this session. ",
      "La curiosité apparaît clairement comme un point fort dans cette session. "
    );
  }

  if (report.care > 1) {
    text += pickInline(
      "There are also signs of caring and responsibility in some of the choices made. ",
      "On observe également des signes d'attention et de responsabilité dans plusieurs choix. "
    );
  }

  text += pickInline("\n\nLearning Style:\n\n", "\n\nStyle d'apprentissage :\n\n");

  if (report.exploration >= 3) {
    text += pickInline(
      "This child appears to be a hands-on learner who benefits from exploring environments and discovering patterns independently. ",
      "Cet enfant semble être un apprenant actif qui bénéficie de l'exploration et de la découverte indépendante. "
    );
  } else if (report.analysis >= 3) {
    text += pickInline(
      "This child appears to be a reflective learner who benefits from structure, clear logic, and time to process information. ",
      "Cet enfant semble être un apprenant réflexif qui profite d'une structure claire et de temps pour traiter l'information. "
    );
  } else {
    text += pickInline(
      "This child shows a balanced approach to learning, mixing exploration with careful thinking. ",
      "Cet enfant montre une approche équilibrée, mêlant exploration et réflexion. "
    );
  }

  return text;
}

// ─── Trend report ─────────────────────────────────────────────────────────────

/**
 * Aggregates all history sessions into a trend profile.
 * @param {import("./wonderWorldProgress").SessionEntry[]} allHistory
 * @param {{ lang: string, pickInline: Function }} ctx
 * @returns {{ type, avgAccuracy, totals, narrative } | null}
 */
export function generateTrendReport(allHistory, { lang, pickInline }) {
  if (allHistory.length === 0) return null;

  const totals = allHistory.reduce(
    (acc, item) => {
      acc.questions += item.questionCount || 0;
      acc.correct += item.correctCount || 0;
      acc.exploration += item.exploration || 0;
      acc.analysis += item.analysis || 0;
      acc.risk += item.risk || 0;
      acc.curiosity += item.curiosity || 0;
      acc.care += item.care || 0;
      acc.logic += item.logic || 0;
      acc.stars += item.stars || 0;
      return acc;
    },
    { questions: 0, correct: 0, exploration: 0, analysis: 0, risk: 0, curiosity: 0, care: 0, logic: 0, stars: 0 }
  );

  const avgAccuracy = totals.questions > 0
    ? Math.round((totals.correct / totals.questions) * 100)
    : 0;

  const type = getCognitiveType(totals);
  const typeLabel = getCognitiveTypeLabel(type, lang);

  let narrative =
    lang === "fr"
      ? `Sur les ${allHistory.length} dernières sessions, votre enfant présente un profil plutôt ${typeLabel.toLowerCase()}. `
      : lang === "zh"
      ? `在最近的 ${allHistory.length} 次游戏中，您的孩子整体呈现出较明显的${typeLabel}特点。 `
      : `Across ${allHistory.length} recent sessions, your child shows a ${typeLabel.toLowerCase()} pattern. `;

  if (avgAccuracy >= 80) {
    narrative += pickInline(
      "Their academic performance in the game has been strong overall. ",
      "Ses performances globales dans le jeu sont solides. "
    );
  } else if (avgAccuracy >= 50) {
    narrative += pickInline(
      "Their overall academic performance looks steady, with room to grow through repetition and confidence-building. ",
      "Ses résultats globaux sont stables, avec une belle marge de progression grâce à la pratique régulière. "
    );
  } else {
    narrative += pickInline(
      "Their overall performance suggests they may benefit from slower pacing and more repetition. ",
      "Ses résultats globaux suggèrent qu'un rythme plus progressif et davantage de répétition seraient utiles. "
    );
  }

  if (totals.exploration > totals.analysis) {
    narrative += pickInline(
      "They often choose discovery-oriented paths and seem motivated by novelty. ",
      "Votre enfant choisit souvent des chemins orientés découverte et semble motivé par la nouveauté. "
    );
  } else {
    narrative += pickInline(
      "They often show careful observation and structured thinking before acting. ",
      "Votre enfant montre souvent une observation attentive et une pensée structurée avant d'agir. "
    );
  }

  if (totals.risk > 3) {
    narrative += pickInline(
      "They are relatively comfortable with uncertainty and experimentation. ",
      "Votre enfant est relativement à l'aise avec l'incertitude et l'expérimentation. "
    );
  } else {
    narrative += pickInline(
      "They tend to prefer safer and more controlled decisions. ",
      "Votre enfant tend à privilégier des décisions plus sûres et mieux maîtrisées. "
    );
  }

  narrative += pickInline(
    "This pattern can help you choose activities that fit their natural learning style.",
    "Ce profil peut vous aider à choisir des activités adaptées à sa manière naturelle d'apprendre."
  );

  return { type, avgAccuracy, totals, narrative };
}

// ─── Report text builder ──────────────────────────────────────────────────────

/**
 * Builds the full parent report as a plain-text string.
 *
 * @param {{
 *   history: SessionEntry[],
 *   lang: string,
 *   t: Function,
 *   pickInline: Function,
 *   getWorldDisplayName: Function
 * }} ctx
 */
export function buildCurrentReportText({ history, lang, t, pickInline, getWorldDisplayName }) {
  if (history.length === 0) return "";

  const generatedAt = new Date().toLocaleString();
  const latest = history[0];
  const trend = generateTrendReport(history, { lang, pickInline });

  const lines = [
    t("report_file_title"),
    `${t("generated_at")}: ${generatedAt}`,
    "",
    t("latest_session"),
    `${t("world")}: ${getWorldDisplayName(latest.world)}`,
    `${t("played")}: ${latest.createdAt}`,
    `${t("questions_answered")}: ${latest.questionCount}`,
    `${t("accuracy")}: ${latest.accuracyRate}%`,
    `${t("stars_earned")}: ${latest.stars}`,
    "",
    t("latest_cognitive_profile"),
    `${t("type")}: ${getCognitiveTypeLabel(getCognitiveType(latest), lang)}`,
    `${t("exploration")}: ${latest.exploration}`,
    `${t("analysis")}: ${latest.analysis}`,
    `${t("risk")}: ${latest.risk}`,
    `${t("curiosity")}: ${latest.curiosity}`,
    `${t("care_responsibility")}: ${latest.care}`,
    `${t("logic_preference")}: ${latest.logic}`,
    "",
    t("latest_session_narrative"),
    generateSummary(latest, { lang, pickInline }),
    ""
  ];

  if (trend) {
    lines.push(t("overall_trend"));
    lines.push(`${t("profile_type")}: ${getCognitiveTypeLabel(trend.type, lang)}`);
    lines.push(`${t("average_accuracy")}: ${trend.avgAccuracy}%`);
    lines.push(`${t("total_sessions")}: ${history.length}`);
    lines.push(`${t("total_stars")}: ${trend.totals.stars}`);
    lines.push("");
    lines.push(trend.narrative);
    lines.push("");
  }

  lines.push(t("recent_sessions"));
  history.slice(0, 5).forEach((item) => {
    lines.push(
      `${getWorldDisplayName(item.world)} - ${item.accuracyRate}% ${t("accuracy_inline")} - ${item.createdAt}`
    );
  });

  return lines.join("\n");
}

/**
 * Builds and triggers a .txt download of the parent report.
 * Identical to the original downloadReportTxt() in App.js, but takes
 * all context as arguments instead of closing over component state.
 */
export function downloadReportTxt(ctx) {
  const text = buildCurrentReportText(ctx);

  if (!text) return;

  const safeTime = new Date().toISOString().replace(/[:.]/g, "-");
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = `wonderworld-parent-report-${safeTime}.txt`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}
